import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom"; 
import * as api from "../../api/index";
import { FiArrowLeft, FiCheckCircle, FiClock, FiCreditCard } from "react-icons/fi";

const AdminWithdrawals = () => {
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [statusFilter, setStatusFilter] = useState("pending"); // Default to pending requests

  const fetchWithdrawals = async () => {
    try {
      setLoading(true);
      setError("");
      // Using the admin endpoint, filtering by type 'withdrawal'
      const res = await api.getAdminPayments(page, 20, "withdrawal", statusFilter);
      if (res.data.success) {
        setTransactions(res.data.payments || []);
        setTotalPages(res.data.pages || 1);
      }
    } catch (err) {
      setError("⚠️ Could not load withdrawal requests.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWithdrawals();
  }, [page, statusFilter]);

  const handleApprove = async (id) => {
    if (!window.confirm("Confirm that you have manually sent this money to the user's bank?")) return;
    
    try {
      await api.acceptWithdraw(id); // This should update status to 'success'
      alert("Withdrawal marked as Success");
      fetchWithdrawals();
    } catch (err) {
      alert("Failed to update status.");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      {/* Header */}
      <div className="max-w-6xl mx-auto flex items-center justify-between mb-8">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-gray-600 font-bold hover:text-black transition">
          <FiArrowLeft size={20} /> <span className="text-xs uppercase tracking-widest">Dashboard</span>
        </button>
        <h1 className="text-2xl font-black text-gray-900 uppercase tracking-tighter">Withdrawal Requests</h1>
        <div className="w-20" /> 
      </div>

      {/* Status Tabs */}
      <div className="max-w-6xl mx-auto flex gap-2 mb-6 bg-white p-2 rounded-2xl border border-gray-100 w-fit">
        {["pending", "success", "failed"].map((status) => (
          <button
            key={status}
            onClick={() => { setStatusFilter(status); setPage(1); }}
            className={`px-6 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${
              statusFilter === status ? "bg-orange-600 text-white shadow-lg shadow-orange-200" : "text-gray-400 hover:bg-gray-50"
            }`}
          >
            {status}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><div className="w-10 h-10 border-4 border-orange-600 border-t-transparent rounded-full animate-spin"></div></div>
      ) : (
        <div className="max-w-6xl mx-auto grid gap-4">
          {transactions.length === 0 ? (
            <div className="bg-white p-20 text-center rounded-[2rem] border border-dashed border-gray-200">
               <p className="text-gray-400 font-bold italic">No {statusFilter} withdrawals found.</p>
            </div>
          ) : (
            transactions.map((tx) => (
              <div key={tx._id} className="bg-white p-6 rounded-[2rem] border border-gray-100 shadow-sm flex flex-wrap md:flex-nowrap items-center justify-between gap-6 hover:shadow-md transition">
                {/* User Info */}
                <div className="flex items-center gap-4 min-w-[200px]">
                  <div className="w-12 h-12 bg-gray-100 rounded-2xl flex items-center justify-center text-orange-600">
                    <FiCreditCard size={24} />
                  </div>
                  <div>
                    <p className="font-black text-gray-900 leading-none mb-1">{tx.email}</p>
                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">{new Date(tx.createdAt).toLocaleString()}</p>
                  </div>
                </div>

                {/* Bank Details from Metadata */}
                <div className="bg-gray-50 px-6 py-3 rounded-2xl border border-gray-100 flex-1 min-w-[250px]">
                  <p className="text-[9px] font-black text-orange-600 uppercase mb-1">Destination Bank</p>
                  <p className="text-sm font-black text-gray-800">
                    {tx.metadata?.bankName} — <span className="text-orange-600">{tx.metadata?.accountNumber}</span>
                  </p>
                  <p className="text-[11px] font-bold text-gray-500 uppercase">{tx.metadata?.accountHolder}</p>
                </div>

                {/* Amount */}
                <div className="text-center md:text-right min-w-[120px]">
                  <p className="text-[10px] font-black text-gray-400 uppercase mb-1">Payout Amount</p>
                  <p className="text-xl font-black text-gray-900">₦{tx.amount?.toLocaleString()}</p>
                </div>

                {/* Action */}
                <div className="w-full md:w-auto">
                  {tx.status === "pending" ? (
                    <button
                      onClick={() => handleApprove(tx._id)}
                      className="w-full md:w-auto bg-green-600 text-white px-8 py-4 rounded-2xl font-black text-[11px] uppercase tracking-widest hover:bg-green-700 transition shadow-lg shadow-green-100 flex items-center justify-center gap-2"
                    >
                      <FiCheckCircle size={16} /> Mark as Paid
                    </button>
                  ) : (
                    <div className={`flex items-center gap-2 px-4 py-2 rounded-xl border text-[10px] font-black uppercase ${
                      tx.status === 'success' ? 'bg-green-50 border-green-100 text-green-600' : 'bg-red-50 border-red-100 text-red-600'
                    }`}>
                      {tx.status === 'success' ? <FiCheckCircle /> : <FiClock />} {tx.status}
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-4 mt-10">
          <button disabled={page === 1} onClick={() => setPage(p => p - 1)} className="p-3 bg-white rounded-xl border border-gray-100 disabled:opacity-30 font-black text-[10px] uppercase">Prev</button>
          <span className="font-black text-xs uppercase text-gray-400">Page {page} of {totalPages}</span>
          <button disabled={page === totalPages} onClick={() => setPage(p => p + 1)} className="p-3 bg-white rounded-xl border border-gray-100 disabled:opacity-30 font-black text-[10px] uppercase">Next</button>
        </div>
      )}
    </div>
  );
};

export default AdminWithdrawals;